// src/components/Gnb/Gnb.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    NavigationMenu,
    NavigationMenuList,
    NavigationMenuItem,
    NavigationMenuTrigger,
    NavigationMenuContent,
    NavigationMenuLink,
} from '@radix-ui/react-navigation-menu';
import LogoButton from '@/components/Button/LogoButton';
import ProfileImage from '../ProfileImage';
import GnbMyInfo from './GnbMyInfo';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { CaptionRegular, OverlineLight } from '../../lib/font';
import { colors } from '../../lib/colors';
import NewProfileImage from '../NewProfileImage';
import SvgIcon from '../SvgIcon';
import AlertCenterBell from '../AlertCenter/AlertCenterBell';
import { usePayrollAuthContext } from '../../context/PayrollAuthContext';
import { usePayrollAuth } from '../../hook/usePayrollAuth';
import useTossPayment from '../../hook/useTossPayment';
import TabLockPromotionTooltip from './TabLockPromotionTooltip';
import { hasSeenTabLockPromotion, markTabLockPromotionAsSeen } from '../../lib/tabLockPromotion';
import { useTeamFeatureStatus } from '@/features/team';
import { useOpsBriefingAccess } from '@/services/useOpsBriefingService';
import { useOpsContext } from '@/scenes/OpsBriefing/useOpsContext';
import { CENTER_VIEW_BANNER_HEIGHT } from '../../context/AccessModeContext';
import { canShowNav } from './navGate';
import { NAV_CONFIG, NavItem, NavSubmenuItem } from './navConfig';

const USAGE_GUIDE_URL = 'https://mustmove.notion.site/Web-bace33141cdc49799eba3ea109512ec3';


type NavMode = 'full' | 'text' | 'icon';


export default function Gnb({
    onClickTerm,
    onClickPermissionModal,
    onClickTabLockModal,
    onClickMemberNotificationModal,
    authObject,
}: {
    onClickTerm: () => void;
    onClickPermissionModal?: () => void;
    onClickTabLockModal?: () => void;
    onClickMemberNotificationModal?: () => void;
    authObject?: any;
}) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [auth] = useState(getItemWithExpiration('authObject'));
    const [showInfo, setShowInfo] = useState(false);
    const [isBellOpen, setIsBellOpen] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);
    const logoRef = useRef<HTMLDivElement>(null);
    const navContentRef = useRef<HTMLDivElement>(null);
    const rightSectionRef = useRef<HTMLDivElement>(null);
    const profileButtonRef = useRef<HTMLDivElement>(null);
    const isActive = (prefix: string, submenu?: NavSubmenuItem[]) => {
        if (pathname.startsWith(prefix)) return true;
        if (submenu) return submenu.some((item) => pathname.startsWith(item.to));
        return false;
    };
    const { isUnlocked, lockSection } = usePayrollAuthContext();
    usePayrollAuth();
    const { requestCardChange } = useTossPayment();

    const isPlanAborted = authObject?.usableStatus === 'planAborted';
    const isCenterViewer = authObject?.accessMode === 'view' || authObject?.role === 'center_viewer';
    const [showPromotion, setShowPromotion] = useState(false);
    const name = auth?.name + ' 님' || '이름없음';
    const centerName = auth?.centerName || '';
    const centerId = authObject?.centerId || auth?.centerId || '';
    const { data: teamFeatureStatus } = useTeamFeatureStatus({ centerId, enabled: !!centerId });
    const showSuperAdminCenterFeatures = teamFeatureStatus?.enabled === true;
    /*
     * 데일리 브리핑은 team-feature-status 와 **다른 값**을 봅니다.
     * 그 값은 "프랜차이즈 산하인가"라서 전체 센터의 약 절반이 해당됩니다 —
     * 그걸로 게이트를 걸면 배포하는 순간 절반의 센터 원장에게 메뉴가 보입니다.
     *
     * allowed 는 서버가 명시적으로 열렸다고 답했을 때만 true 입니다.
     * 로딩 중·조회 실패는 전부 false 라 메뉴가 나오지 않습니다 (fail-closed).
     * 브리핑 화면들과 같은 인증 정보를 써야 메뉴와 라우트 판정이 갈리지 않습니다.
     */
    const opsCtx = useOpsContext();
    const { allowed: opsBriefingEnabled } = useOpsBriefingAccess(opsCtx);
    const navItems = NAV_CONFIG.map((nav) => ({
        ...nav,
        submenu: nav.submenu
            ?.filter((item) =>
                canShowNav(item, {
                    showSuperAdminCenterFeatures,
                    isTrial: auth?.adminId === 'trial',
                    opsBriefingEnabled,
                })
            )
            .map((item) => ({
                ...item,
                isNew: item.isNew && (!item.isNewSuperAdminCenterOnly || showSuperAdminCenterFeatures),
            })),
    })).filter((nav) =>
        canShowNav(nav, {
            showSuperAdminCenterFeatures,
            isTrial: auth?.adminId === 'trial',
            opsBriefingEnabled,
        })
    );

    const [navMode, setNavMode] = useState<NavMode>('full');
    // 측정 트리거 — 컨테이너 크기/구성 변경 시 bump 해서 측정 effect 를 강제 재실행
    const [measureToken, setMeasureToken] = useState(0);

    // 헤더 크기 변화 감시 (resize 이벤트보다 정확하고, rAF 로 자연스럽게 throttle 됨)
    useEffect(() => {
        const headerEl = headerRef.current;
        if (!headerEl || typeof ResizeObserver === 'undefined') return;
        let frameId: number | null = null;
        const observer = new ResizeObserver(() => {
            if (frameId !== null) cancelAnimationFrame(frameId);
            frameId = requestAnimationFrame(() => {
                setNavMode('full');
                setMeasureToken((t) => t + 1);
            });
        });
        observer.observe(headerEl);
        return () => {
            if (frameId !== null) cancelAnimationFrame(frameId);
            observer.disconnect();
        };
    }, []);

    // GNB 구성이 바뀔 때(권한·플랜·이름·센터·노출 플래그) 다시 'full' 부터 측정
    // 브리핑 판정이 늦게 도착하면 메뉴가 하나 늘어난다. 폭을 다시 재지 않으면 넘친다.
    useEffect(() => {
        setNavMode('full');
        setMeasureToken((t) => t + 1);
    }, [
        isUnlocked,
        isPlanAborted,
        auth?.adminId,
        name,
        centerName,
        showSuperAdminCenterFeatures,
        opsBriefingEnabled,
    ]);

    // 실제 폭 측정 후 한 단계 다운그레이드 (full → text → icon)
    useEffect(() => {
        const frame = window.requestAnimationFrame(() => {
            const headerWidth = headerRef.current?.clientWidth ?? 0;
            const logoWidth = logoRef.current?.offsetWidth ?? 0;
            const navWidth = navContentRef.current?.scrollWidth ?? 0;
            const rightWidth = rightSectionRef.current?.offsetWidth ?? 0;

            if (!headerWidth || !navWidth || !rightWidth) return;

            const requiredWidth = logoWidth + navWidth + rightWidth + 48;
            if (requiredWidth <= headerWidth) return;

            setNavMode((prev) => {
                if (prev === 'full') return 'text';
                if (prev === 'text') return 'icon';
                return prev;
            });
        });

        return () => window.cancelAnimationFrame(frame);
    }, [navMode, measureToken]);

    const navGap = navMode === 'full' ? 'gap-[10px]' : navMode === 'text' ? 'gap-[8px]' : 'gap-[6px]';
    const getNavItemClass = (active: boolean) =>
        [
            'cursor-pointer relative',
            'flex items-center whitespace-nowrap border-[1.5px] transition-all duration-200',
            navMode === 'icon'
                ? 'h-[44px] w-[44px] justify-center rounded-[14px]'
                : 'h-[46px] px-[14px] rounded-[14px] gap-[8px]',
            active
                ? 'border-[#BFDBFE] bg-[#EFF6FF] shadow-[0_10px_24px_rgba(37,99,235,0.12)]'
                : 'border-[#E2E8F0] bg-[rgba(255,255,255,0.78)] text-gray-700 shadow-[0_6px_16px_rgba(15,23,42,0.04)] hover:border-[#CBD5E1] hover:bg-[#FFFFFF] hover:shadow-[0_8px_18px_rgba(15,23,42,0.06)]',
        ].join(' ');

    return (
        <div
            ref={headerRef}
            className="fixed z-50 flex h-[88px] w-full items-center justify-between gap-[24px] px-[18px] min-[1360px]:px-[24px] min-[1600px]:pl-[48px] min-[1600px]:pr-[40px]"
            style={{
                top: isCenterViewer ? CENTER_VIEW_BANNER_HEIGHT : 0,
                background: 'rgba(255, 255, 255, 0.88)',
                borderBottom: '1px solid rgba(203, 213, 225, 0.9)',
                boxShadow: '0 10px 32px rgba(15, 23, 42, 0.05)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
            }}
        >
            {/* left: logo + nav */}
            <div className="flex min-w-0 flex-1 items-center gap-[24px]">
                <div ref={logoRef} className="flex-shrink-0">
                    <LogoButton callBack={() => navigate('/dash')} />
                </div>

                <div className="min-w-0 flex-1">
                    <div ref={navContentRef} className="w-max">
                        <NavigationMenu delayDuration={100} skipDelayDuration={300}>
                            <NavigationMenuList className={`flex items-center ${navGap}`}>
                                {navItems.map((nav) =>
                                    nav.submenu ? (
                                        <NavigationMenuItem key={nav.label}>
                                            <NavigationMenuTrigger
                                                className={getNavItemClass(isActive(nav.prefix, nav.submenu))}
                                                title={navMode === 'icon' ? nav.label : undefined}
                                                onClick={() => {
                                                    const defaultRoute = nav.submenu?.[0]?.to;
                                                    if (defaultRoute) navigate(defaultRoute);
                                                }}
                                            >
                                                {navMode !== 'text' && nav.icon && (
                                                    <SvgIcon
                                                        name={nav.icon as any}
                                                        size={navMode === 'icon' ? 20 : 16}
                                                        fill={isActive(nav.prefix, nav.submenu) ? colors.primary : '#64748B'}
                                                        stroke={isActive(nav.prefix, nav.submenu) ? colors.primary : '#64748B'}
                                                    />
                                                )}
                                                {navMode !== 'icon' && (
                                                    <RegularText
                                                        fontSize={16}
                                                        lineHeight={24}
                                                        color={isActive(nav.prefix, nav.submenu) ? colors.primary : '#334155'}
                                                    >
                                                        {nav.label}
                                                    </RegularText>
                                                )}
                                                {nav.isNew && navMode !== 'icon' && (
                                                    <span className="absolute -top-[7px] right-[8px] inline-flex items-center px-[5px] py-[1px] rounded-full bg-[#EF4444] text-white text-[9px] font-bold leading-none tracking-wide shadow-[0_2px_6px_rgba(239,68,68,0.35)]">
                                                        NEW
                                                    </span>
                                                )}
                                                {nav.isNew && navMode === 'icon' && (
                                                    <span className="absolute -top-[2px] -right-[2px] w-[9px] h-[9px] rounded-full bg-[#EF4444] border-[1.5px] border-white" />
                                                )}
                                            </NavigationMenuTrigger>
                                            <NavigationMenuContent
                                                className="absolute mt-[10px] rounded-[18px] py-[14px] px-[12px] space-y-[6px] min-w-[180px]"
                                                style={{
                                                    background: 'rgba(255,255,255,0.96)',
                                                    border: '1px solid #E2E8F0',
                                                    boxShadow: '0 20px 40px rgba(15,23,42,0.14)',
                                                    backdropFilter: 'blur(16px)',
                                                    WebkitBackdropFilter: 'blur(16px)',
                                                }}
                                            >
                                                {nav.submenu.map((item) => (
                                                    <NavigationMenuLink
                                                        key={item.to}
                                                        className={`block whitespace-nowrap cursor-pointer px-[12px] py-[10px] rounded-[12px] transition-all duration-150 ${
                                                            pathname.startsWith(item.to)
                                                                ? 'bg-[#EFF6FF]'
                                                                : 'hover:bg-[#F8FAFC]'
                                                        }`}
                                                        onClick={() => navigate(item.to)}
                                                    >
                                                        <span className="flex items-center gap-[8px]">
                                                            <RegularText
                                                                fontSize={14}
                                                                lineHeight={22}
                                                                color={pathname.startsWith(item.to) ? colors.primary : '#334155'}
                                                            >
                                                                {item.name}
                                                            </RegularText>
                                                            {item.isNew && (
                                                                <span className="inline-flex items-center rounded-full bg-[#EF4444] px-[5px] py-[1px] text-[9px] font-bold leading-none tracking-wide text-white">
                                                                    NEW
                                                                </span>
                                                            )}
                                                        </span>
                                                    </NavigationMenuLink>
                                                ))}
                                            </NavigationMenuContent>
                                        </NavigationMenuItem>
                                    ) : (
                                        <NavigationMenuItem key={nav.label}>
                                            <NavigationMenuTrigger asChild>
                                                <button
                                                    onClick={() => nav.to && navigate(nav.to)}
                                                    className={getNavItemClass(isActive(nav.prefix, nav.submenu))}
                                                    title={navMode === 'icon' ? nav.label : undefined}
                                                >
                                                    {navMode !== 'text' && nav.icon && (
                                                        <SvgIcon
                                                            name={nav.icon as any}
                                                            size={navMode === 'icon' ? 20 : 16}
                                                            fill={isActive(nav.prefix, nav.submenu) ? colors.primary : '#64748B'}
                                                            stroke={isActive(nav.prefix, nav.submenu) ? colors.primary : '#64748B'}
                                                        />
                                                    )}
                                                    {navMode !== 'icon' && (
                                                        <RegularText
                                                            fontSize={15}
                                                            lineHeight={22}
                                                            color={isActive(nav.prefix, nav.submenu) ? colors.primary : '#334155'}
                                                        >
                                                            {nav.label}
                                                        </RegularText>
                                                    )}
                                                    {nav.isNew && navMode !== 'icon' && (
                                                        <span className="absolute -top-[7px] right-[8px] inline-flex items-center px-[5px] py-[1px] rounded-full bg-[#EF4444] text-white text-[9px] font-bold leading-none tracking-wide shadow-[0_2px_6px_rgba(239,68,68,0.35)]">
                                                            NEW
                                                        </span>
                                                    )}
                                                    {nav.isNew && navMode === 'icon' && (
                                                        <span className="absolute -top-[2px] -right-[2px] w-[9px] h-[9px] rounded-full bg-[#EF4444] border-[1.5px] border-white" />
                                                    )}
                                                </button>
                                            </NavigationMenuTrigger>
                                        </NavigationMenuItem>
                                    ),
                                )}
                            </NavigationMenuList>
                        </NavigationMenu>
                    </div>
                </div>
            </div>

            {/* right: lock / help / profile */}
            <div ref={rightSectionRef} className="flex items-center space-x-4 flex-shrink-0">
                {isPlanAborted && (
                    <div className="flex items-center rounded-[8px] bg-[#FFEBEE] px-[14px] py-[6px] space-x-[10px]">
                        <CaptionRegular style={{ color: '#D32F2F', whiteSpace: 'nowrap' }}>
                            결제 갱신이 필요합니다
                        </CaptionRegular>
                        <button
                            className="flex items-center px-[12px] py-[4px] rounded-[6px] bg-[#D32F2F] hover:bg-[#B71C1C] transition-colors duration-200 cursor-pointer"
                            onClick={() => authObject?.id && requestCardChange(authObject.id)}
                        >
                            <CaptionRegular style={{ color: '#FFFFFF', whiteSpace: 'nowrap' }}>
                                결제 수단 변경
                            </CaptionRegular>
                        </button>
                    </div>
                )}
                {isUnlocked && (
                    <div
                        className="flex items-center cursor-pointer px-[10px] py-[7px] rounded-[12px] bg-[#FFF7ED] hover:bg-[#FFEDD5] border border-[#FED7AA] transition-colors duration-200"
                        onClick={() => lockSection()}
                        title="클릭하면 즉시 잠금됩니다"
                    >
                        <SvgIcon name="SvgLockOpen" size={13} fill="#E65100" stroke="#E65100" className="mr-[3px]" />
                        <CaptionRegular style={{ color: '#E65100', fontSize: 12 }}>잠금</CaptionRegular>
                    </div>
                )}
                <AlertCenterBell isOpen={isBellOpen} onOpenChange={setIsBellOpen} />

                <div className="relative ">
                    <Row
                        ref={profileButtonRef}
                        onClick={() => setShowInfo(!showInfo)}
                        className={`flex py-[9px] items-center justify-start rounded-[16px] cursor-pointer pl-[9px] pr-[30px] transition-all duration-200 ${
                            showInfo
                                ? 'bg-[#EFF6FF] border border-[#BFDBFE]'
                                : 'bg-[#FFFFFF] hover:bg-[#F8FAFC] border border-[#E2E8F0]'
                        }`}
                        style={{
                            boxShadow: showInfo
                                ? '0 10px 24px rgba(37, 99, 235, 0.12)'
                                : '0 10px 24px rgba(15, 23, 42, 0.06)',
                        }}
                    >
                        <NewProfileImage
                            imageUrl={auth?.profileImageUrl || ''}
                            size={32}
                            isWhiteCircle
                            imageFill="#BDC4CC"
                            className="flex mr-[6px]"
                            circleColor="#FFFFFF"
                            iconSize={18}
                        />
                        <Col className={`flex max-w-[100px]`}>
                            <MediumText fontSize={13} lineHeight={17} letterSpacing={-0.3} color={colors.Text_1} className="whitespace-nowrap overflow-hidden text-ellipsis">
                                {name}
                            </MediumText>
                            <DemiLightText
                                fontSize={11}
                                lineHeight={15}
                                color={colors.LAVEL_4}
                                letterSpacing={-0.3}
                                className="whitespace-nowrap overflow-hidden text-ellipsis"
                            >
                                {centerName}
                            </DemiLightText>
                        </Col>
                        <SvgIcon
                            name="SvgDownStrokeArrow"
                            size={14}
                            fill={colors.trans}
                            stroke={showInfo ? colors.primary : colors.LAVEL_4}
                            className={`absolute right-[10px] transition-transform duration-200 ${showInfo ? 'rotate-180' : 'rotate-0'}`}
                        />
                    </Row>
                    {showInfo && (
                        <GnbMyInfo
                            authObject={auth}
                            setIsVisibleInfo={setShowInfo}
                            navigate={navigate}
                            onClickTerm={onClickTerm}
                            profileButtonRef={profileButtonRef}
                            onClickPermissionModal={onClickPermissionModal}
                            onClickTabLockModal={onClickTabLockModal}
                            onClickMemberNotificationModal={onClickMemberNotificationModal}
                            onClickAlertCenter={() => {
                                setShowInfo(false);
                                setIsBellOpen(true);
                            }}
                            onClickUsageGuide={() => {
                                window.open(USAGE_GUIDE_URL, '_blank');
                                setShowInfo(false);
                            }}
                        />
                    )}
                    {showPromotion && !showInfo && (
                        <TabLockPromotionTooltip
                            onClickSetup={() => {
                                onClickTabLockModal && onClickTabLockModal();
                                markTabLockPromotionAsSeen();
                                setShowPromotion(false);
                            }}
                            onDismiss={() => {
                                markTabLockPromotionAsSeen();
                                setShowPromotion(false);
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
